import { useState, KeyboardEvent, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  useCourseTagsLibrary, 
  useModuleTagsLibrary, 
  useLessonTagsLibrary 
} from '@/hooks/useLmsTags';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TAG_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Cyan', value: '#06b6d4' },
];

interface LmsTagInputProps {
  entityType: 'course' | 'module' | 'lesson';
  entityId: string | null;
  brandId: string;
  currentTagIds?: string[];
  onTagsChange?: (tagIds: string[]) => void;
}

export function LmsTagInput({ entityType, entityId, brandId, currentTagIds = [], onTagsChange }: LmsTagInputProps) {
  const [tagName, setTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0].value);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>(currentTagIds);
  
  // Use appropriate hook based on entity type
  const courseLibrary = useCourseTagsLibrary(entityType === 'course' ? brandId : null);
  const moduleLibrary = useModuleTagsLibrary(entityType === 'module' ? brandId : null);
  const lessonLibrary = useLessonTagsLibrary(entityType === 'lesson' ? brandId : null);

  const { tags: tagLibrary, isLoading, createTag } = 
    entityType === 'course' ? courseLibrary :
    entityType === 'module' ? moduleLibrary :
    lessonLibrary;

  // Sync with prop changes
  useEffect(() => {
    setAssignedTagIds(currentTagIds);
  }, [currentTagIds]);

  const assignedTags = tagLibrary.filter(tag => assignedTagIds.includes(tag.id));

  const handleCreateAndAssignTag = () => {
    const trimmedName = tagName.trim();
    if (!trimmedName || !entityId) return;

    // Check if tag already exists in library
    const existingTag = tagLibrary.find(t => t.name.toLowerCase() === trimmedName.toLowerCase());
    
    if (existingTag) {
      // Just assign the existing tag
      handleAssignTag(existingTag.id);
      setTagName('');
    } else {
      // Create new tag and assign it
      createTag({ name: trimmedName, color: selectedColor });
      setTagName('');
    }
  };

  const handleAssignTag = async (tagId: string) => {
    if (!entityId) return;
    
    const newTagIds = [...assignedTagIds, tagId];
    
    const tableName = 
      entityType === 'course' ? 'os_lms_courses' :
      entityType === 'module' ? 'os_lms_modules' :
      'os_lms_lessons';
    
    const { error } = await supabase
      .from(tableName)
      .update({ tag_ids: newTagIds })
      .eq('id', entityId);

    if (error) {
      toast.error('Failed to assign tag');
      return;
    }

    setAssignedTagIds(newTagIds);
    onTagsChange?.(newTagIds);
    toast.success('Tag assigned');
  };

  const handleUnassignTag = async (tagId: string) => {
    if (!entityId) return;
    
    const newTagIds = assignedTagIds.filter(id => id !== tagId);
    
    const tableName = 
      entityType === 'course' ? 'os_lms_courses' :
      entityType === 'module' ? 'os_lms_modules' :
      'os_lms_lessons';
    
    const { error } = await supabase
      .from(tableName)
      .update({ tag_ids: newTagIds })
      .eq('id', entityId);

    if (error) {
      toast.error('Failed to unassign tag');
      return;
    }

    setAssignedTagIds(newTagIds);
    onTagsChange?.(newTagIds);
    toast.success('Tag unassigned');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateAndAssignTag();
    }
  };

  const unassignedTags = tagLibrary.filter(tag => !assignedTagIds.includes(tag.id));

  if (!entityId) {
    return (
      <div className="space-y-2">
        <Label>Tags</Label>
        <p className="text-sm text-muted-foreground">
          Save the {entityType} first to add tags
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label>Tags</Label>
      
      {/* Display assigned tags */}
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {isLoading ? (
          <span className="text-sm text-muted-foreground">Loading tags...</span>
        ) : assignedTags.length > 0 ? (
          assignedTags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              style={{ backgroundColor: tag.color + '20', color: tag.color, borderColor: tag.color }}
              className="gap-1 pr-1"
            >
              {tag.name}
              <button
                onClick={() => handleUnassignTag(tag.id)}
                className="ml-1 hover:bg-black/10 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">No tags assigned</span>
        )}
      </div>

      {/* Assign existing tags */}
      {unassignedTags.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Assign from library</Label>
          <div className="flex flex-wrap gap-2">
            {unassignedTags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                style={{ borderColor: tag.color, color: tag.color }}
                className="cursor-pointer hover:bg-accent"
                onClick={() => handleAssignTag(tag.id)}
              >
                {tag.name}
                <Plus className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Create and assign new tag */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Create new tag</Label>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input
              placeholder="Enter tag name"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <Select value={selectedColor} onValueChange={setSelectedColor}>
            <SelectTrigger className="w-[120px]">
              <SelectValue>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: selectedColor }}
                  />
                  <span className="text-sm">
                    {TAG_COLORS.find(c => c.value === selectedColor)?.name}
                  </span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TAG_COLORS.map((color) => (
                <SelectItem key={color.value} value={color.value}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: color.value }}
                    />
                    <span>{color.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            type="button" 
            size="sm" 
            onClick={handleCreateAndAssignTag}
            disabled={!tagName.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
